/**
 * "Ask a question" on an approval from another app (Procurement today): ask before you approve or reject.
 * The approval stays pending and Approve / Reject stay available; the answer comes back here and to your Inbox.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HelpCircle, MessageCircleQuestion, Reply } from "lucide-react";
import { useState } from "react";
import { post } from "../../lib/api";
import { ErrorNote, fmtDate } from "../ui";

export interface ThreadEntry { id: string; type: "question" | "answer"; text: string; kind: string | null; by: string | null; at: string; projectSet: string | null; source: string }
export type QuestionState = "awaiting_answer" | "answered" | null;

const QUICK: Array<{ kind: string; text: string }> = [
  { kind: "which_project", text: "Which project is this for?" },
  { kind: "why_supplier", text: "Why this supplier?" },
  { kind: "cheaper_quote", text: "Is there a cheaper quote?" },
  { kind: "needed_by", text: "When is it needed?" },
];

export function QuestionChip({ state }: { state: QuestionState | undefined }) {
  if (!state) return null;
  return state === "awaiting_answer"
    ? <span className="chip bg-amber-50 text-xs text-amber-800">Waiting for an answer</span>
    : <span className="chip bg-emerald-50 text-xs text-emerald-800">Answered</span>;
}

/** The thread, plus the Ask box when you can ask and no question is open. */
export function ApprovalQuestions({ id, thread, state, canAsk }: { id: string; thread: ThreadEntry[]; state: QuestionState; canAsk: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("other");
  const ask = useMutation({
    mutationFn: () => post(`/api/approvals/${id}/questions`, { question: text.trim(), kind }),
    onSuccess: () => { setOpen(false); setText(""); setKind("other"); for (const k of ["approval", "approvals", "inbox", "my-day"]) void qc.invalidateQueries({ queryKey: [k] }); },
  });
  if (!thread.length && !canAsk) return null;
  return (
    <div className="mt-3 space-y-2">
      {thread.length > 0 && (
        <ul className="space-y-1.5 rounded-lg bg-black/[0.02] p-2.5 text-sm">
          {thread.map((t) => (
            <li key={t.id} className="flex items-start gap-2">
              {t.type === "question" ? <HelpCircle size={15} className="mt-0.5 shrink-0 text-amber-600" /> : <Reply size={15} className="mt-0.5 shrink-0 text-emerald-600" />}
              <span className="min-w-0 flex-1">
                <span className="whitespace-pre-wrap">{t.text}</span>
                {t.projectSet && <span className="chip ml-1.5 text-xs">Project: {t.projectSet}</span>}
                <span className="muted block text-xs">{t.type === "question" ? "Asked" : "Answered"} by {t.by ?? "someone"} · {fmtDate(t.at)}</span>
              </span>
            </li>
          ))}
          {state === "awaiting_answer" && <li className="muted pl-6 text-xs">Waiting for the answer. You can still approve or reject.</li>}
        </ul>
      )}
      {canAsk && state !== "awaiting_answer" && (open ? (
        <div className="space-y-2 rounded-lg border border-[var(--line)] p-2.5">
          <div className="flex flex-wrap gap-1.5">
            {QUICK.map((q) => (
              <button key={q.kind} type="button" className={`chip cursor-pointer text-xs ${kind === q.kind && text === q.text ? "!border-brand-600 !bg-brand-50 text-brand-700" : ""}`} onClick={() => { setText(q.text); setKind(q.kind); }}>{q.text}</button>
            ))}
          </div>
          <textarea className="input min-h-[3.5rem] text-sm" autoFocus maxLength={1000} placeholder="Your question to the person who raised it" value={text} onChange={(e) => { setText(e.target.value); if (!QUICK.some((q) => q.text === e.target.value)) setKind("other"); }} />
          <ErrorNote error={ask.error} />
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-sm" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={text.trim().length < 3 || ask.isPending} onClick={() => ask.mutate()}>{ask.isPending ? "Sending…" : "Send question"}</button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-sm" onClick={() => setOpen(true)} title="Keeps the approval pending; the answer comes back here"><MessageCircleQuestion size={15} /> Ask a question</button>
      ))}
    </div>
  );
}
